import React, { useState } from 'react';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import InputLabel from '@mui/material/InputLabel';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import {
  ActionType,
  ALL,
  BreakdownTab,
  DurationEnum,
  RoleType,
  UserMapDataType,
} from '../../types/analyticTypes';
import { TOP_N_DEFAULT, TOP_N_MAX, TOP_N_MIN, clampTopN } from '../../utils/topN';
import { getUsersForRole } from './getUsersForRole';

type BreakdownAnalyticsFilterProps = {
  option: DurationEnum;
  actions: ActionType[];
  role: RoleType;
  users: string[];
  topN: number;
  activeTab: BreakdownTab;
  userMap: UserMapDataType;
  onFilterChange: (
    selectedOption: DurationEnum,
    selectedActions: ActionType[],
    selectedRole: RoleType,
    selectedUsers: string[],
    selectedTopN: number
  ) => void;
};

const BreakdownAnalyticsFilter: React.FC<BreakdownAnalyticsFilterProps> = ({
  option,
  actions,
  role,
  users,
  topN,
  activeTab,
  userMap,
  onFilterChange,
}) => {
  const [selectedOption, setSelectedOption] = useState<DurationEnum>(option);
  const [selectedActions, setSelectedActions] = useState<ActionType[]>(actions);
  const [selectedRole, setSelectedRole] = useState<RoleType>(role);
  const [selectedUsers, setSelectedUsers] = useState<string[]>(users);
  const [selectedTopN, setSelectedTopN] = useState<number>(clampTopN(topN));

  const userOptions = React.useMemo(
    () => getUsersForRole(userMap, selectedRole),
    [userMap, selectedRole]
  );
  const noUsersForRole = selectedRole !== RoleType.all && userOptions.length <= 1;
  const isUserTab = activeTab === BreakdownTab.user;

  const handleDurationChange = React.useCallback((event: SelectChangeEvent) => {
    setSelectedOption(event.target.value as DurationEnum);
  }, []);

  const handleActionTypeChange = React.useCallback((event: SelectChangeEvent<ActionType[]>) => {
    const updatedValues = event.target.value as ActionType[];

    const finalValues =
      updatedValues.includes(ActionType.all) &&
      updatedValues[updatedValues.length - 1] === ActionType.all
        ? [ActionType.all]
        : updatedValues.filter((type) => type !== ActionType.all);

    setSelectedActions(finalValues);
  }, []);

  const handleRoleTypeChange = React.useCallback(
    (event: SelectChangeEvent) => {
      const newRole = event.target.value as RoleType;
      setSelectedRole(newRole);

      const allowedUsers = getUsersForRole(userMap, newRole);

      setSelectedUsers((prevUsers) => {
        const validSpecificUsers = prevUsers.filter(
          (email) => email !== ALL && allowedUsers.includes(email)
        );
        return validSpecificUsers.length > 0 ? validSpecificUsers : [ALL];
      });
    },
    [userMap]
  );

  const handleUserChange = React.useCallback((_event: React.SyntheticEvent, value: string[]) => {
    if (value.length === 0) {
      setSelectedUsers([ALL]);
      return;
    }

    const finalValues =
      value.includes(ALL) && value[value.length - 1] === ALL
        ? [ALL]
        : value.filter((email) => email !== ALL);

    setSelectedUsers(finalValues);
  }, []);

  const handleTopNChange = React.useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.value === '') {
      setSelectedTopN(TOP_N_DEFAULT);
      return;
    }
    setSelectedTopN(clampTopN(Number(event.target.value)));
  }, []);

  const getDropdownLabel = (selected: ActionType[]): string => {
    if (selected.length === 1) {
      return selected[0] === ActionType.all ? 'All actions' : selected[0];
    }

    return `${selected.length} actions selected`;
  };

  const onApply = React.useCallback(() => {
    onFilterChange(
      selectedOption,
      selectedActions,
      selectedRole,
      selectedUsers,
      clampTopN(selectedTopN)
    );
  }, [onFilterChange, selectedOption, selectedActions, selectedRole, selectedUsers, selectedTopN]);

  return (
    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <FormControl size="medium" style={{ minWidth: '100px' }}>
        <InputLabel>Duration</InputLabel>
        <Select
          value={selectedOption}
          label="duration"
          sx={{ textTransform: 'capitalize' }}
          onChange={handleDurationChange}
        >
          {Object.values(DurationEnum).map((durationOption) => (
            <MenuItem
              key={durationOption}
              value={durationOption}
              sx={{ textTransform: 'capitalize' }}
            >
              {durationOption}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="medium" style={{ minWidth: '300px' }}>
        <InputLabel>Action types</InputLabel>
        <Select
          multiple
          value={selectedActions}
          label="action types"
          onChange={handleActionTypeChange}
          renderValue={getDropdownLabel}
        >
          {Object.values(ActionType).map((actionOption) => (
            <MenuItem key={actionOption} value={actionOption}>
              {actionOption === ActionType.all ? 'All actions' : actionOption}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <FormControl size="medium" style={{ minWidth: '200px' }}>
        <InputLabel>Role</InputLabel>
        <Select value={selectedRole} label="Role" onChange={handleRoleTypeChange}>
          {Object.keys(RoleType).map((role) => (
            <MenuItem key={role} value={role}>
              {role === RoleType.all ? 'All roles' : role}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <Autocomplete
        size="medium"
        sx={{ minWidth: '320px' }}
        multiple
        options={userOptions}
        value={selectedUsers}
        onChange={handleUserChange}
        disabled={noUsersForRole}
        disableClearable
        getOptionLabel={(userOption) => (userOption === ALL ? 'All users' : userOption)}
        isOptionEqualToValue={(userOption, value) => userOption === value}
        renderOption={(optionProps, userOption) => (
          <Box component="li" {...optionProps} key={userOption}>
            {userOption === ALL ? (
              <Typography variant="body2" color="text.secondary">
                All users
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                <span>{userOption}</span>
                {userMap[userOption]?.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    variant="outlined"
                    sx={{ height: 18, fontSize: 10 }}
                  />
                ))}
              </Stack>
            )}
          </Box>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Users"
            error={noUsersForRole}
            helperText={
              noUsersForRole ? 'Not found users with this role - please change the role' : undefined
            }
          />
        )}
      />
      {isUserTab && (
        <FormControl size="medium" style={{ minWidth: '110px' }}>
          <TextField
            label="Top N"
            type="number"
            value={selectedTopN}
            onChange={handleTopNChange}
            inputProps={{ min: TOP_N_MIN, max: TOP_N_MAX, step: 1 }}
          />
        </FormControl>
      )}
      <Button variant="contained" onClick={onApply}>
        Apply
      </Button>
    </Box>
  );
};

export default BreakdownAnalyticsFilter;
